import React from 'react';
import SparkOverlay from 'sharedComponents/UI/SparkOverlay';
import SparkChatBubbleInner from './SparkChatBubbleInner';
import { FileRejection } from 'react-dropzone/.';
import ChatMessageBubble from './ChatMessageBubble';
import type { FooterActions } from './ChatMessageBubble';
import type { AttachmentCardType } from './AttachmentCard';
import { useInfiniteChatMessages } from 'hooks/chats/useInfiniteChatMessages';
import ChatInfiniteScroll from 'sharedComponents/UI/ChatInfiniteScroll';
import ChatsPluginDrawer from './ChatsPluginDrawer';
import { useDialog } from 'hooks/common';
import { FunctionCall } from 'types/Chats/ChatMessage';

const MemoizedChatMessage = React.memo(ChatMessageBubble);

type Props = {
  attachments?: AttachmentCardType[];
  chatID: string;
  onAttachmentRemove?: (
    cardType: AttachmentCardType['cardType'],
    id: AttachmentCardType['id']
  ) => void;
  onFileUpload: (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => Promise<(string | undefined)[] | undefined>;
  onSendMessage: (value: string) => void;
  onClose: () => void;
  open: boolean;
  onActionClick?:
    | ((type: FooterActions, messageID: string, secondaryID?: string) => void)
    | undefined;
  isExpandedViewOpen?: boolean;
  onAddAttachments: (attachments: AttachmentCardType) => void;
};

function ChatHistoryExpandedView({
  open,
  onClose,
  chatID,
  attachments,
  onAttachmentRemove,
  onFileUpload,
  onSendMessage,
  onActionClick,
  isExpandedViewOpen = false,
  onAddAttachments,
}: Props) {
  const {
    open: isOpenPluginInfoDrawer,
    openDialog: openPluginInfoDialog,
    closeDialog: closePluginInfoDialog,
  } = useDialog(false);

  const {
    chatMessages: infiniteChatMessages,
    isLoading,
    hasNextPage,
    loadMoreMessages,
    isFetchingNextPage,
  } = useInfiniteChatMessages(chatID);

  const handleLoadOlderMessages = React.useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    loadMoreMessages && loadMoreMessages();
  }, [loadMoreMessages, hasNextPage, isFetchingNextPage]);

  const handlePluginClick = (PluginsUsed: FunctionCall) => {
    openPluginInfoDialog();
  };
  return (
    <SparkOverlay
      onClose={onClose}
      open={open}
      childrenContainerStyles={{
        justifyContent: 'center',
        overflow: 'hidden',
      }}
      actions={
        <SparkChatBubbleInner
          chatID={chatID}
          attachments={attachments}
          onFileUpload={onFileUpload}
          onAttachmentRemove={onAttachmentRemove}
          openExpandedView={() => {}}
          onSendMessage={onSendMessage}
          expandable={false}
          onAddAttachments={onAddAttachments}
        />
      }
    >
      <ChatInfiniteScroll
        onLoadOlderMessages={handleLoadOlderMessages}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
      >
        {infiniteChatMessages?.map((message, index, messages) => {
          return (
            <MemoizedChatMessage
              id={message.id}
              key={message.id}
              message={message.attributes.content}
              isUserMessage={message.attributes.authorRoleID === 1}
              isStreaming={message.attributes.isStreaming || false}
              isLastMessage={messages.length === index + 1}
              error={message.attributes.error}
              onActionClick={onActionClick}
              chatMessageRatingTypeID={
                message.attributes.chatMessageRatingTypeID
              }
              authorRoleID={message.attributes.authorRoleID}
              isExpandedViewOpen={isExpandedViewOpen}
              PluginsUsed={message.attributes?.functionCalls || []}
              handlePluginClicked={handlePluginClick}
              pluginsPill={message.attributes.pluginsPill}
              boardWorkingPill={message.attributes.boardWorkingPill}
            />
          );
        })}
        {/* {isOpenPluginInfoDrawer && (
          <ChatsPluginDrawer
            open={isOpenPluginInfoDrawer}
            onClose={closePluginInfoDialog}
            chatMessagePlugins={[]}
            drawerHeight='91vh'
          />
        )} */}
      </ChatInfiniteScroll>
    </SparkOverlay>
  );
}

export default ChatHistoryExpandedView;
